<template>
  <!-- Whole card links to the post's own page; hidden entirely if the post has no resolvable author -->
  <RouterLink v-if="postAuthor" class="suggested-post-compact" :to="linkOnPost(post.id)">
    <div class="suggested-post-compact__layout flex items-start justify-between gap-3">
      <!-- Left: Avatar + User Info -->
      <div class="suggested-post-compact__left-section suggested-post-compact__grid flex-1 min-w-0">
        <!-- Avatar -->
        <div class="suggested-post-compact__avatar-cell flex-shrink-0">
          <BaseAvatar :storage-id="postAuthor.avatarFile?.storageId" :verified="postAuthor.verified" :href="postAuthorRoute"
            :isAdmin="postAuthor.isAdmin" :rating="postAuthor.rating" :isCurrentUser="false" />
        </div>

        <!-- Tier badge directly under avatar (disabled for now) -->
        <!-- <div v-if="postAuthor.rating" class="suggested-post-compact__badge-cell">
          <div class="suggested-post-compact__badge-aligner">
            <ReputationBadge :score="postAuthor.rating" :showTierName="true" :isCurrentUser="false" :tierSet="postAuthorTierSet"
              size="micro" class="suggested-post-compact__tier-badge" />
          </div>
        </div> -->

        <!-- Name and username line -->
        <div class="suggested-post-compact__name-cell suggested-post-compact__name-line flex items-center gap-2 flex-wrap">
          <div class="suggested-post-compact__author-name hover:underline overflow-hidden text-ellipsis cursor-pointer">
            {{ postAuthorFullName }}
          </div>
          <div v-if="postAuthor.username"
            class="suggested-post-compact__username text-[0.8rem] text-[var(--color-text-02)] hover:underline cursor-pointer">
            @{{ postAuthor.username }}
          </div>
        </div>

        <!-- Post content preview: title, body text, or a placeholder if the post has neither -->
        <div class="suggested-post-compact__bio-cell suggested-post-compact__content-section mt-1">
          <div class="suggested-post-compact__preview text-text02 text-xs text-left">
            <span v-if="post.title" class="suggested-post-compact__title font-semibold block">
              {{ truncatePostText(post.title) }}
            </span>
            <span v-if="post.text" class="suggested-post-compact__text">
              {{ truncatePostText(post.text) }}
            </span>
            <span v-if="!post.title && !post.text" class="italic opacity-60">
              No content available
            </span>
          </div>
        </div>
      </div>

      <!-- Right: Post date -->
      <div class="suggested-post-compact__right-section flex-shrink-0">
        <span class="suggested-post-compact__date text-xs text-[var(--color-text-02)]">{{ formattedTime }}</span>
      </div>
    </div>
  </RouterLink>
</template>

<script setup>
import { abbreviateNumber } from "@/helpers/helpers.js";
import { computed } from "vue";
import { getRelativeTime } from "@/helpers/relativeTimeHelper.js";
import { useStore } from "vuex";
import ReputationBadge from "@/components/ReputationBadge.vue";

/**
 * @prop {Object} post - The suggested post to preview (title/text/publishedAt, plus `post.authorUser`).
 * @prop {Function} onCloseDropdown - Optional callback the parent can pass to close a containing dropdown; not invoked within this component itself.
 */
const props = defineProps({
  post: {
    type: Object,
    default: null,
  },
  onCloseDropdown: {
    type: Function,
    default: null
  }
});

const store = useStore();
/** The post's author user record, if present. */
const postAuthor = computed(() => props.post.authorUser || null);

// Tier set for reputation badges
/** Active reputation-tier set (currently unused since the tier badge markup below is commented out). */
const postAuthorTierSet = computed(() => store.getters.getTierSet);

/** Author's display name: "First Last" (each truncated to 12 chars) if a first name exists, else their username. */
const postAuthorFullName = computed(() => {
  let firstName = postAuthor.value.firstName;
  let lastName = postAuthor.value.lastName;
  let maxWidth = 12; // Increased for better display

  firstName =
    firstName?.length <= maxWidth
      ? firstName
      : firstName?.slice(0, maxWidth) + "..";
  lastName =
    lastName?.length <= maxWidth
      ? lastName
      : lastName?.slice(0, maxWidth) + "..";
  if (!postAuthor.value.firstName) {
    return postAuthor.value.username;
  } else {
    return firstName + " " + lastName;
  }
});

/** Profile route for the post's author: by username if known, else by id. */
const postAuthorRoute = computed(() => {
  return !postAuthor.value.username
    ? `/profile/${postAuthor.value?.id}`
    : `/user/${postAuthor.value?.username}`;
});

/**
 * Builds the router-link target for opening this post's own page.
 * @param {string} id - The post's id
 * @returns {Object} a vue-router location object, targeting the 'reply' or 'post' route depending on `post.type`
 */
const linkOnPost = (id) => {
  return {
    name: props.post.type === "reply" ? "reply" : "post",
    params: { id },
  };
};

/**
 * Strips HTML tags from post title/text and truncates it to 80 characters for the compact preview.
 * @param {string} text - Raw (possibly HTML) text to clean and truncate
 * @returns {string} plain-text excerpt, or an empty string if `text` is empty
 */
const truncatePostText = (text) => {
  if (!text) {
    return "";
  }
  const cleanText = text.replace(/<.*?>/g, " ").trim();
  return cleanText.slice(0, 80); // Shortened for compact display
};

/** The shared "current time" tick from the store, used to keep the relative timestamp live. */
const now = computed(() => store.getters.getCurrentTime);
/** Post's publish time formatted as a compact, auto-updating relative string. */
const formattedTime = computed(() => {
  return getRelativeTime(props.post.publishedAt, now, true);
});
</script>

<style scoped lang="scss">
.suggested-post-compact {
  padding: 12px 8px;
  display: block;
  transition: 0.2s ease-in;
  border-bottom: 1px solid var(--color-box-border);
  border-radius: 0;

  &:hover {
    background-color: var(--color-theme-wise-hover, rgba(0, 0, 0, 0.02));
  }
}

.suggested-post-compact__layout {
  width: 100%;
}

.suggested-post-compact__left-section {
  display: grid;
  grid-template-columns: 40px 1fr;
  grid-template-rows: auto auto auto;
  grid-template-areas:
    "avatar name"
    "badge name"
    ". bio";
  gap: 2px 8px;
  align-items: start;
}

.suggested-post-compact__avatar-cell {
  grid-area: avatar;
  display: flex;
  justify-content: center;
}

.suggested-post-compact__badge-cell {
  grid-area: badge;
  display: flex;
  justify-content: center;

  .suggested-post-compact__badge-aligner {
    display: flex;
    justify-content: center;
    width: 100%;
  }
}

.suggested-post-compact__name-cell {
  grid-area: name;
  align-self: center;
}

.suggested-post-compact__bio-cell {
  grid-area: bio;
  align-self: start;
}

.suggested-post-compact__name-line {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.suggested-post-compact__author-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-foreground-primary);
  text-decoration: none;
}

.suggested-post-compact__username {
  font-size: 0.75rem;
  color: var(--color-text-02);
  text-decoration: none;
}

.suggested-post-compact__content-section {
  margin-top: 4px;

  .suggested-post-compact__preview {
    line-height: 1.3;
    color: var(--color-text-02);

    .suggested-post-compact__title {
      font-weight: 600;
      color: var(--color-foreground-primary);
      margin-bottom: 2px;
      font-size: 0.8rem;
    }

    .suggested-post-compact__text {
      color: var(--color-text-02);
      font-size: 0.8rem;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.suggested-post-compact__right-section {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.suggested-post-compact__tier-badge {
  font-size: 0.65rem !important;
  padding: 1px 4px !important;
  min-width: auto !important;
}
</style>
